import React, { useState, useEffect } from 'react';
import { Mail, CheckCircle, Clock, Plus, X, AlertCircle, Loader } from 'lucide-react';
import { useNotification } from '../../contexts/NotificationContext';
import { getApiUrl } from '../../config/api';
import axios from 'axios';

const EmailConfirmation = ({ onComplete, onSkip, wizardData, isCompleted }) => {
  const { showNotification } = useNotification();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [confirmedEmails, setConfirmedEmails] = useState([]);
  const [pendingEmails, setPendingEmails] = useState([]);

  useEffect(() => {
    fetchConfirmedEmails();
  }, []);

  // Poll for confirmed emails and check on window focus
  useEffect(() => {
    // Only poll if there are pending emails and no confirmed emails yet
    if (pendingEmails.length > 0 && confirmedEmails.length === 0) {
      // Poll every 3 seconds
      const intervalId = setInterval(() => {
        fetchConfirmedEmails();
      }, 3000);

      // Also check when window regains focus (user returns from email)
      const handleFocus = () => {
        fetchConfirmedEmails();
      };

      window.addEventListener('focus', handleFocus);

      return () => {
        clearInterval(intervalId);
        window.removeEventListener('focus', handleFocus);
      };
    }
  }, [pendingEmails.length, confirmedEmails.length]);

  const fetchConfirmedEmails = async () => {
    try {
      const response = await axios.get(getApiUrl('/api/entries/confirmed-emails'), {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      const newConfirmedEmails = response.data.emails || [];
      setConfirmedEmails(newConfirmedEmails);
      
      // Remove confirmed emails from pending list
      if (newConfirmedEmails.length > 0 && pendingEmails.length > 0) {
        setPendingEmails(prev => 
          prev.filter(pending => 
            !newConfirmedEmails.some(confirmed => 
              confirmed.email.toLowerCase() === pending.email.toLowerCase()
            )
          )
        );
      }
    } catch (error) {
      console.error('Error fetching confirmed emails:', error);
    }
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSendConfirmation = async () => {
    if (!email || !validateEmail(email)) {
      showNotification('Please enter a valid email address', 'error');
      return;
    }

    // Check if email is already confirmed
    if (confirmedEmails.some(e => e.email.toLowerCase() === email.toLowerCase())) {
      showNotification('This email is already confirmed', 'info');
      setEmail('');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        getApiUrl('/api/entries/confirm-email'),
        { email },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (response.data.alreadyApproved) {
        showNotification('This email is already confirmed', 'info');
        await fetchConfirmedEmails();
      } else {
        showNotification('Confirmation email sent! Check your inbox.', 'success');
        setPendingEmails(prev => [...prev, { email, sentAt: new Date() }]);
      }
      setEmail('');
    } catch (error) {
      console.error('Error sending confirmation email:', error);
      showNotification(
        error.response?.data?.error || 'Failed to send confirmation email',
        'error'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = async () => {
    if (confirmedEmails.length === 0) {
      showNotification('Please confirm at least one email address', 'error');
      return;
    }

    // Check if this is coming from first-time import flow
    // If wizardData indicates first-time import, trigger sync
    const isFirstTimeImport = wizardData?.isFirstTimeImport || false;

    if (isFirstTimeImport) {
      setSyncing(true);
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          throw new Error('No authentication token found');
        }

        showNotification('Starting data import...', 'info');

        // Trigger sync for all three types in parallel
        const syncPromises = [
          axios.get(getApiUrl('/api/entries/delta-sync'), {
            headers: { Authorization: `Bearer ${token}` }
          }).catch(err => {
            console.error('Error syncing entries:', err);
            return { error: 'entries', message: err.response?.data?.error || 'Failed to sync entries' };
          }),
          axios.get(getApiUrl('/api/results/delta-sync'), {
            headers: { Authorization: `Bearer ${token}` }
          }).catch(err => {
            console.error('Error syncing results:', err);
            return { error: 'results', message: err.response?.data?.error || 'Failed to sync results' };
          }),
          axios.get(getApiUrl('/api/associations/memberships/delta-sync'), {
            headers: { Authorization: `Bearer ${token}` }
          }).catch(err => {
            console.error('Error syncing memberships:', err);
            return { error: 'memberships', message: err.response?.data?.error || 'Failed to sync memberships' };
          })
        ];

        const results = await Promise.allSettled(syncPromises);
        
        const syncResults = {
          entries: { success: false, message: '', summary: null },
          results: { success: false, message: '', summary: null },
          memberships: { success: false, message: '', summary: null }
        };

        results.forEach((result, index) => {
          const type = ['entries', 'results', 'memberships'][index];
          if (result.status === 'fulfilled' && !result.value.error) {
            syncResults[type] = { 
              success: true, 
              message: result.value.data?.message || `${type} synced successfully`,
              summary: result.value.data?.summary || null
            };
          } else {
            const error = result.status === 'fulfilled' ? result.value : result.reason;
            syncResults[type] = { 
              success: false, 
              message: error.message || `Failed to sync ${type}`,
              summary: null
            };
          }
        });

        // Show summary notification
        const successCount = Object.values(syncResults).filter(r => r.success).length;
        if (successCount === 3) {
          showNotification('All data imported successfully!', 'success');
        } else if (successCount > 0) {
          showNotification(`Imported ${successCount} of 3 data types. Check Settings for details.`, 'info');
        } else {
          showNotification('Import completed with some errors. Check Settings for details.', 'warning');
        }

        setSyncing(false);
        
        // Pass sync results to onComplete
        if (onComplete) {
          onComplete('emailConfirmation', { 
            confirmedEmails: confirmedEmails.length,
            syncResults: syncResults
          });
        }
      } catch (error) {
        console.error('Error during sync:', error);
        showNotification('Error importing data. You can sync manually from Settings.', 'error');
        setSyncing(false);
        if (onComplete) {
          onComplete('emailConfirmation', { confirmedEmails: confirmedEmails.length });
        }
      }
    } else {
      if (onComplete) {
        onComplete('emailConfirmation', { confirmedEmails: confirmedEmails.length });
      }
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="text-center">
        <Mail className="w-12 h-12 text-blue-600 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Confirm Your Email Address</h2>
        <p className="text-gray-600">
          Enter the email address you use with KCIRodeo associations. We'll send you a confirmation email to verify ownership.
        </p>
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start">
          <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-1">Why confirm your email?</p>
            <p>Once confirmed, MyTrailer can automatically sync your entries, results, and memberships from KCIRodeo. You can confirm multiple email addresses if you use different emails for different associations.</p>
          </div>
        </div>
      </div>

      {/* Email Input */}
      <div className="bg-white border border-gray-200 rounded-lg p-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Add Email Address</h3>
        <div className="flex gap-2">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="your.email@example.com"
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleSendConfirmation();
              }
            }}
          />
          <button
            onClick={handleSendConfirmation}
            disabled={loading || !email}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Sending...
              </>
            ) : (
              <>
                <Mail className="w-4 h-4" />
                Send Confirmation
              </>
            )}
          </button>
        </div>
      </div>

      {/* Confirmed Emails List */}
      {confirmedEmails.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Confirmed Emails ({confirmedEmails.length})</h3>
          <div className="space-y-2">
            {confirmedEmails.map((item, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <div>
                    <p className="font-medium text-gray-900">{item.email}</p>
                            <p className="text-xs text-gray-600">
                              Confirmed {item.confirmedAt ? new Date(item.confirmedAt).toLocaleDateString() : 'recently'}
                            </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Pending Emails List */}
      {pendingEmails.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Pending Confirmation ({pendingEmails.length})</h3>
          <div className="space-y-2">
            {pendingEmails.map((item, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-3 bg-yellow-50 border border-yellow-200 rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <Clock className="w-5 h-5 text-yellow-600" />
                  <div>
                    <p className="font-medium text-gray-900">{item.email}</p>
                    <p className="text-xs text-gray-600">
                      Check your email and click the confirmation link
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setPendingEmails(prev => prev.filter((_, i) => i !== index));
                    fetchConfirmedEmails();
                  }}
                  className="p-1 text-gray-400 hover:text-gray-600"
                  title="Refresh status"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex justify-between pt-4">
        <button
          onClick={onSkip}
          disabled={syncing}
          className="px-6 py-2 text-gray-600 hover:text-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Skip for Now
        </button>
        <button
          onClick={handleComplete}
          disabled={confirmedEmails.length === 0 || syncing}
          className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
        >
          {syncing ? (
            <>
              <Loader className="w-4 h-4 animate-spin" />
              Importing...
            </>
          ) : (
            'Continue'
          )}
        </button>
      </div>
    </div>
  );
};

export default EmailConfirmation;

